import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react";
import { ControlProps } from "@jsonforms/core";
import { useEffect, useState, useRef, useCallback } from "react";
import { Input } from "../../../../../../shadcn/components/ui/input";
import { getPincodeDetails } from "services";

const isPincodeBasedFetchingEnabled =
  process.env.NEXT_PUBLIC_ENABLE_PINCODE_BASED_FETCHING === "true";

// When flag is OFF: simple text input (old behavior)
// When flag is ON: auto-fetch on 6 digits, prefilled state/city

export const PincodeFieldTypeRenderer = (props: ControlProps) => {
  const { data, handleChange, path, schema, visible, uischema, errors } = props;
  const ctx = useJsonForms();
  const title = schema?.title;
  const inputFieldClassName = uischema?.options?.inputFieldClassName;
  const outerContainerClassName = uischema?.options?.outerContainerClassName;
  const additionalData = uischema?.options?.additionalData;
  const [value, setValue] = useState<any>(data === undefined ? "" : data);
  const [customError, setCustomError] = useState("");
  const [loading, setLoading] = useState(false);
  const [autoFillStatus, setAutoFillStatus] = useState<
    "idle" | "loading" | "success" | "no-match"
  >("idle");
  const positionRef = useRef<HTMLInputElement>(null);
  const lastLookedUpPincode = useRef<string>("");
  const abortControllerRef = useRef<AbortController | null>(null);
  const autoFillTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const show = uischema?.options?.show;
  const disableClearValue = additionalData?.disableClearValue;

  // Clear auto-fill status after 3 seconds
  const scheduleStatusReset = useCallback((status: "success" | "no-match") => {
    setAutoFillStatus(status);
    if (autoFillTimerRef.current) clearTimeout(autoFillTimerRef.current);
    autoFillTimerRef.current = setTimeout(() => {
      setAutoFillStatus("idle");
    }, 3000);
  }, []);

  // Clear state, city, and district when pincode changes
  const clearAutoFilledFields = useCallback(() => {
    handleChange("state", {});
    handleChange("city", {});
    handleChange("district", {});
  }, [handleChange]);

  // Handle pincode lookup (only when flag is ON)
  const handlePincodeChange = useCallback(
    async (pincode: string) => {
      setValue(pincode);
      handleChange(path, pincode);

      if (pincode.length === 6 && isPincodeBasedFetchingEnabled) {
        // Skip if we already looked up this pincode
        if (lastLookedUpPincode.current === pincode) return;
        lastLookedUpPincode.current = pincode;

        // Cancel any in-flight request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        setLoading(true);
        setAutoFillStatus("loading");

        try {
          const pincodeData: any = await getPincodeDetails(pincode);

          // Check if this request was cancelled
          if (abortControllerRef.current?.signal.aborted) return;

          if (pincodeData?.resolved) {
            // Pincode found with resolved data
            const {
              state_uuid,
              state_name,
              state_slug,
              city_uuid,
              city_name,
              city_slug,
            } = pincodeData.resolved;

            const statePath = "state";
            const cityPath = "city";
            const districtPath = "district";

            // Set state
            if (state_uuid) {
              handleChange(statePath, {
                label: state_name,
                value: state_uuid,
                slug: state_slug,
              });
            } else {
              handleChange(statePath, {
                name: pincodeData.state_name || state_name,
              });
            }

            // Set city with delay to ensure state is set first
            setTimeout(() => {
              if (city_uuid) {
                handleChange(cityPath, {
                  label: city_name,
                  value: city_uuid,
                  slug: city_slug,
                });
              } else {
                handleChange(cityPath, {
                  name: pincodeData.city || city_name,
                });
              }

              // Also store district name (needed for city creation)
              // The district_name comes from pincodeData.district
              if (pincodeData.district) {
                handleChange(districtPath, {
                  name: pincodeData.district,
                });
              }
            }, 100);

            scheduleStatusReset("success");
          } else {
            // Pincode not found - clear state/city
            clearAutoFilledFields();
            scheduleStatusReset("no-match");
          }

          setCustomError("");
        } catch (error: any) {
          if (abortControllerRef.current?.signal.aborted) return;
          console.error("Error fetching pincode:", error);
          setCustomError("");
          scheduleStatusReset("no-match");
        } finally {
          setLoading(false);
        }
      } else if (pincode.length > 6) {
        setCustomError("PIN cannot exceed 6 digits");
      } else {
        // Pincode changed to <6 digits or flag is OFF - clear auto-filled fields
        lastLookedUpPincode.current = "";
        setCustomError("");
        setAutoFillStatus("idle");
        if (isPincodeBasedFetchingEnabled) {
          clearAutoFilledFields();
        }
      }
    },
    [ctx, handleChange, path, clearAutoFilledFields, scheduleStatusReset],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (autoFillTimerRef.current) clearTimeout(autoFillTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (disableClearValue === true) return;
    if (show === false) {
      handleChange(path, "");
      setValue("");
    }
  }, [show]);

  useEffect(() => {
    const errorData = ctx?.core?.errors;
    let flag = false;
    errorData?.forEach((error: any) => {
      if (error?.instancePath === "/" + path) {
        setCustomError(error?.message);
        flag = true;
      } else if (
        error?.instancePath === "" &&
        error?.params?.missingProperty === path
      ) {
        setCustomError("Required Field");
        flag = true;
      }
    });

    if (!flag) setCustomError("");
    else {
      if (positionRef.current) {
        positionRef.current.scrollIntoView({
          behavior: "smooth",
        });
      }
    }
  }, [ctx?.core?.errors]);

  return visible && show ? (
    <>
      <div
        ref={positionRef}
        className={` relative w-full ${outerContainerClassName}`}
      >
        <Input
          id="pincode-input"
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={value}
          onChange={(e) => {
            const numericValue = e.target.value.replace(/\D/g, "");
            handlePincodeChange(numericValue);
          }}
          onBlur={(e) => {
            if (
              value?.length === 6 &&
              lastLookedUpPincode.current !== value &&
              isPincodeBasedFetchingEnabled
            ) {
              handlePincodeChange(value);
            }
          }}
          placeholder={loading ? "Loading..." : "Enter 6-digit pincode"}
          disabled={loading}
          className={`bg-white py-4 peer pl-4 autofill:bg-white focus:border-brand-orange focus:outline-none focus:ring-0 pb-[6px] h-[50px] outline-none border border-[#EEEEEE] w-full font-openSans font-normal text-web-body-sm leading-4 text-brand-textLightGrey ${inputFieldClassName}`}
        />
        <label
          htmlFor="pincode-input"
          className={`absolute font-openSans font-normal text-web-body-sm leading-3 mid:text-web-body-lg mid:leading-5 ${
            !ctx?.core?.data?.[path]
              ? "text-brand-borderGrey"
              : "text-transparent"
          } left-4 top-4 pointer-events-none transition-all duration-300 peer-placeholder-shown:top-1.5 peer-placeholder-shown:text-gray-400 peer-placeholder-shown:text-[10px] peer-focus:top-1.5 peer-focus:text-[10px] peer-focus:leading-[13px] peer-focus:text-brand-orange peer-valid:top-1.5 peer-valid:text-[10px] peer-valid:leading-[13px] peer-valid:text-brand-borderGrey`}
        >
          {title}
        </label>
        {customError && (
          <label
            htmlFor="pincode-input"
            className={"text-brand-textRed text-web-body-sm ml-2"}
          >
            {customError}
          </label>
        )}
        {/* Validation message */}
        {!customError &&
          value &&
          value.length > 0 &&
          value.length < 6 &&
          autoFillStatus === "idle" && (
            <p className="text-[11px] text-brand-textRed font-openSans mt-1 ml-1">
              Pincode must be exactly 6 digits
            </p>
          )}
        {/* Status messages - only show when flag is ON */}
        {isPincodeBasedFetchingEnabled &&
          !customError &&
          autoFillStatus === "idle" &&
          !value && (
            <p className="text-[11px] text-brand-borderGrey font-openSans mt-1 ml-1">
              Enter pincode to auto-fill State &amp; City
            </p>
          )}
        {isPincodeBasedFetchingEnabled && autoFillStatus === "loading" && (
          <p className="text-[11px] text-brand-borderGrey font-openSans mt-1 ml-1">
            Looking up pincode…
          </p>
        )}
        {isPincodeBasedFetchingEnabled && autoFillStatus === "success" && (
          <p className="text-[11px] text-green-600 font-openSans mt-1 ml-1">
            State &amp; City auto-filled from pincode
          </p>
        )}
        {isPincodeBasedFetchingEnabled && autoFillStatus === "no-match" && (
          <p className="text-[11px] text-amber-600 font-openSans mt-1 ml-1">
            Pincode not found in database
          </p>
        )}
      </div>
    </>
  ) : (
    <></>
  );
};

export default withJsonFormsControlProps(PincodeFieldTypeRenderer);
